
import type { QueryData } from '@empathybroker/tsclient-stats/dist/models/queries/query-data'
import type { Kpi } from '../../components/kpi-counter-banner.component.vue'
import type {
  ContextState,
  ConversationalMessages,
  DataTableColumn,
  Pagination,
} from '../../models'

import { QueryTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/query-type.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'

import { DataTable, EpbText, InsightsNoResults, KpiCounterBanner } from '../../components'
import PaginationBar from '../../components/crud/pagination-bar.component.vue'
import InsightCard from '../../components/insights/conversational/insight-card.component.vue'
import TopPerformersItems from '../../components/insights/conversational/top-performers-items.component.vue'
import { UiProgressLinear } from '../../components/keen-ui'
import { mapActions, mapGetters } from '../../composables/utils'
import { AlignEnum, ColorEnum, QueriesTypeEnum } from '../../enums'

import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'

interface TopPerformer {
  query: string
  queryCount: number
  clickCount: number
  clickCountPct: number
}

const CAMPAIGN_FILTER = ['truman']
const NATURAL_CONVERSATION_MIN_LENGTH = 80
const TOP_PERFORMERS_COUNT = 3

export default defineComponent({
  name: 'Conversational',
  components: {
    DataTable,
    EpbText,
    InsightCard,
    InsightsNoResults,
    KpiCounterBanner,
    PaginationBar,
    TopPerformersItems,
    UiProgressLinear,
  },
  setup() {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const baseClass = 'epb-conversational'

    const { loadDistData, loadSessionsData, reset, loadQueriesData, fetchStatsReport } = mapActions(
      ctx.proxy.$store,
      ANALYTICS_CONSTS.moduleName,
      {
        loadDistData: ANALYTICS_CONSTS.actions.loadDistData,
        loadSessionsData: ANALYTICS_CONSTS.actions.loadSessionsData,
        reset: ANALYTICS_CONSTS.actions.reset,
        loadQueriesData: ANALYTICS_CONSTS.actions.loadQueriesData,
        fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
      },
    )

    const { analyticsData } = mapGetters(ctx.proxy.$store, ANALYTICS_CONSTS.moduleName, {
      analyticsData: ANALYTICS_CONSTS.getters.data,
    })

    const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const messages: ConversationalMessages = ctx.proxy.$epb.messages.insights.conversational

    const itemsPerPageOptions = [10, 50, 100, 200]
    const pagination = ref<Pagination>({
      page: 1,
      itemsPerPage: itemsPerPageOptions[0],
      totalItems: 0,
    })
    const loading = ref(false)
    const loadingTable = ref(false)
    const uniqueQueries = ref(0)
    const topPerformers = ref<TopPerformer[]>([])
    const naturalConversations = ref(0)

    const formatNumber = (value: number, options = {}): string => {
      return ctx.proxy.$epb.utils.formatNumber(
        value,
        { maximumFractionDigits: 0, ...options },
        true,
      )
    }

    const formatPercentage = (value: number): string => {
      return `${ctx.proxy.$epb.utils.formatNumber(value, { maximumFractionDigits: 2 }, true)}%`
    }

    const getQueriesPaginationType = (type: QueriesTypeEnum): QueryTypeEnum => {
      return {
        [QueriesTypeEnum.ALL]: QueryTypeEnum.ALL,
        [QueriesTypeEnum.WITH_RESULTS]: QueryTypeEnum.WITH_RESULTS,
        [QueriesTypeEnum.WITHOUT_RESULTS]: QueryTypeEnum.WITHOUT_RESULTS,
      }[type]
    }

    const handleError = (error: unknown): void => {
      ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error))
    }

    const processQueriesData = (queriesData: QueryData[]): void => {
      const uniqueQueriesCount = queriesData.filter(
        (item: QueryData) => item.queryCount === 1,
      ).length
      uniqueQueries.value = uniqueQueriesCount

      const naturalConversationsCount = queriesData.filter(
        (item: QueryData) => item.query && item.query.length > NATURAL_CONVERSATION_MIN_LENGTH,
      ).length
      naturalConversations.value = naturalConversationsCount

      const topPerformersData = queriesData
        .filter((item: QueryData) => item.clickCountPct != null)
        .sort((a: QueryData, b: QueryData) => (b.clickCountPct ?? 0) - (a.clickCountPct ?? 0))
        .slice(0, TOP_PERFORMERS_COUNT)
        .map((item: QueryData) => ({
          query: item.query,
          queryCount: item.queryCount,
          clickCount: item.clickCount,
          clickCountPct: item.clickCountPct,
        }))
      topPerformers.value = topPerformersData
    }

    const loadUniqueQueries = async (totalQueries: number): Promise<void> => {
      if (totalQueries <= 0) {
        return
      }

      try {
        const allQueriesResponse = await fetchStatsReport({
          id: StatsReportsIdsEnum.QUERIES,
          params: {
            type: getQueriesPaginationType(QueriesTypeEnum.ALL),
            rows: totalQueries,
            offset: 0,
            filters: {
              campaign: CAMPAIGN_FILTER,
            },
          },
        })

        if (allQueriesResponse?.data) {
          processQueriesData(allQueriesResponse.data)
        }
      } catch (error) {
        handleError(error)
      } finally {
        loadingTable.value = false
      }
    }

    const loadQueriesTableData = async (): Promise<number> => {
      loadingTable.value = true
      try {
        await loadQueriesData({
          type: QueriesTypeEnum.ALL,
          pagination: pagination.value,
          filters: {
            campaign: CAMPAIGN_FILTER,
          },
        })

        const queriesPaginationResponse = await fetchStatsReport({
          id: StatsReportsIdsEnum.QUERIES_PAGINATION,
          params: {
            type: getQueriesPaginationType(QueriesTypeEnum.ALL),
            filters: {
              campaign: CAMPAIGN_FILTER,
            },
          },
        })

        const totalQueries = queriesPaginationResponse.data.termsCount
        pagination.value.totalItems = totalQueries
        return totalQueries
      } catch (error) {
        handleError(error)
        return 0
      }
    }

    const onPaginationChange = (paginationParam: Pagination): void => {
      pagination.value = paginationParam
      loadQueriesTableData()
    }

    const loadData = async (): Promise<void> => {
      loading.value = true
      try {
        analyticsData.value.overall.dist.currentPeriod = undefined
        analyticsData.value.overall.sessions.currentPeriod = undefined
        await reset()
        await loadDistData({
          selectedId: '',
          filters: {
            campaign: CAMPAIGN_FILTER,
          },
        })
        await loadSessionsData({
          campaign: CAMPAIGN_FILTER,
        })
        loading.value = false
        const totalQueries = await loadQueriesTableData()
        await loadUniqueQueries(totalQueries)
      } catch (error) {
        handleError(error)
      }
    }

    const columns = computed(
      (): Array<DataTableColumn> => [
        {
          id: 'query',
          text: messages.table.columns.query,
          width: '40%',
        },
        {
          id: 'query_count',
          text: messages.table.columns.quantity,
          align: AlignEnum.END,
          width: '20%',
        },
        {
          id: 'click_count_pct',
          text: messages.table.columns.ctr,
          align: AlignEnum.END,
          width: '20%',
        },
        {
          id: 'click_count',
          text: messages.table.columns.clicks,
          align: AlignEnum.END,
          width: '20%',
        },
      ],
    )

    const kpis = computed((): Kpi[] => {
      const distData = analyticsData.value?.overall?.dist?.currentPeriod?.data
      const sessionsData = analyticsData.value?.overall?.sessions?.currentPeriod?.data

      const queries = distData?.queryCount ?? 0
      const sessions = sessionsData?.sessionCount ?? 0
      const queriesPerSession = sessionsData?.queriesPerSession ?? 0
      const ctr = distData?.clickCountPct ?? 0

      return [
        {
          label: 'Queries',
          icon: 'query',
          color: 'yellow-25',
          iconColor: ColorEnum.YELLOW_75,
          value: formatNumber(queries),
        },
        {
          label: 'Sessions',
          icon: 'users',
          color: 'purple-25',
          iconColor: ColorEnum.PURPLE_75,
          value: formatNumber(sessions),
        },
        {
          label: 'Q/Session',
          icon: 'attributes',
          color: 'blue-25',
          iconColor: ColorEnum.BLUE_75,
          value: formatNumber(queriesPerSession, { maximumFractionDigits: 2 }),
        },
        {
          label: 'CTR',
          icon: 'actions',
          color: 'green-25',
          iconColor: ColorEnum.GREEN_75,
          value: formatPercentage(ctr),
        },
      ]
    })

    const tableData = computed(() => {
      const queriesData = analyticsData.value?.queries?.[QueriesTypeEnum.ALL]
      if (!queriesData?.data) {
        return []
      }
      return queriesData.data.map((item: QueryData) => ({
        query: item.query,
        query_count: item.queryCount,
        click_count_pct: item.clickCountPct,
        click_count: item.clickCount,
      }))
    })

    const hasNoResults = computed(() => {
      const distData = analyticsData.value?.overall?.dist?.currentPeriod?.data
      const queries = distData?.queryCount ?? 0
      return queries === 0 && distData !== undefined
    })

    watch(
      () => context.value,
      () => {
        loadData()
      },
      { immediate: true, deep: true },
    )

    return {
      baseClass,
      ColorEnum,
      messages,
      columns,
      kpis,
      tableData,
      pagination,
      itemsPerPageOptions,
      loading,
      loadingTable,
      uniqueQueries,
      topPerformers,
      naturalConversations,
      hasNoResults,
      formatNumber,
      formatPercentage,
      onPaginationChange,
    }
  },
})
